import { useState } from 'react';
import type { MouseEvent } from 'react';

import { useElement, useAfterInitialEffect } from '@pega/cosmos-react-core/lib/hooks';
import { Avatar, Button, Flex, InfoDialog, Text } from '@pega/cosmos-react-core';
import type { InfoDialogProps } from '@pega/cosmos-react-core/lib/components/Dialog/Dialog.types';

import { FieldValueListDemo } from '../FieldValueList/FieldValueList.stories';
import { loadingTimeoutMS } from '../Progress/Progress.mocks';

const mockContent = (
  <Flex container={{ direction: 'column', gap: 2 }}>
    <Text as='p'>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
      labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
      laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in
      voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat
      non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
    </Text>
    <FieldValueListDemo name='Demo field' value='demo value' />
  </Flex>
);

export const InfoDialogMock = () => {
  const [target, setTarget] = useElement<HTMLButtonElement>(null);

  return (
    <>
      <Button
        variant='secondary'
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          setTarget(e.currentTarget);
        }}
      >
        Open dialog
      </Button>

      {target ? (
        <InfoDialog heading='General information' target={target} onDismiss={() => setTarget(null)}>
          {mockContent}
        </InfoDialog>
      ) : null}
    </>
  );
};

export const InfoDialogProgressMock = () => {
  const [target, setTarget] = useElement<HTMLButtonElement>(null);
  const [heading, setHeading] = useState<InfoDialogProps['heading']>();
  const [content, setContent] = useState<InfoDialogProps['children']>(null);

  useAfterInitialEffect(() => {
    if (!target) {
      setHeading(undefined);
      setContent(null);
      return;
    }

    // Mocking a network request to fetch data
    const timeout = window.setTimeout(() => {
      setHeading({
        primary: 'Primary heading',
        secondary: 'Secondary info',
        visual: <Avatar name='Info Dialog' />
      });

      setContent(mockContent);
    }, loadingTimeoutMS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [target]);

  return (
    <>
      <Button
        variant='secondary'
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          setTarget(e.currentTarget);
        }}
      >
        Open dialog
      </Button>

      {target && (
        <InfoDialog
          heading={heading}
          target={target}
          onDismiss={() => setTarget(null)}
          progress={!content}
        >
          {content}
        </InfoDialog>
      )}
    </>
  );
};
